import { getQueryArg } from '@wordpress/url';

import { Bump, BundleComponentRow, BundleItem, Checkout, ChoiceType, LineItem, LineItemData, lineItems, Price, PriceChoice, Product, RecursivePartial } from '../../types';

// Order bundle items by their `position` (the order set in the bundle).
const byPosition = <T extends { position?: number }>(items: T[] = []): T[] => [...items].sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0));

/**
 * A bundle component's line item `quantity` is the total across the whole
 * purchase (per-bundle count × bundle quantity). Divide it back out by the
 * parent quantity to get the per-bundle count the bundle actually defines.
 */
export const getPerBundleQuantity = (component: LineItem, parentQuantity = 1): number => {
  const parentQty = Math.max(Number(parentQuantity) || 1, 1);
  const total = Math.max(Number(component?.quantity) || 1, 1);
  return Math.max(Math.round(total / parentQty), 1);
};

/**
 * Build display rows from bundle component line items.
 *
 * By default every component is shown (variant items render `Name - Variant`,
 * plain items render just `Name`). Pass `showAll = false` for variants-only
 * mode, which lists only the components that have a variant selection.
 */
export const getBundleComponentRowsFromLineItems = (components: LineItem[] = [], parentQuantity = 1, showAll = true, separator = '·'): BundleComponentRow[] => {
  const sep = (separator || '·').trim() || '·';
  return (components || [])
    .map(component => {
      // Prefer the raw options array so we can join with the separator; fall back to the pre-joined display string when it's absent.
      const options = (component?.variant_options || []).filter(Boolean);
      const variants = options.length ? options.join(` ${sep} `) : (component?.variant_display_options || '').trim();
      // Variants-only mode: drop components without a selection.
      if (!showAll && !variants) return null;
      const componentProduct = (component?.price as Price)?.product as Product;
      const name = componentProduct?.name || '';
      if (!name) return null;
      const qty = getPerBundleQuantity(component, parentQuantity);
      return { id: component?.id, name, variants, qty };
    })
    .filter(Boolean) as BundleComponentRow[];
};

/**
 * Build a list of display rows from BundleItems attached to a Product
 * (subscription detail panel — no LineItems exist for components there,
 * only the bundle definition).
 */
export const getBundleComponentRowsFromBundleItems = (items: BundleItem[] = []): BundleComponentRow[] => {
  return byPosition(items)
    .map(item => {
      const componentProduct = item?.component_product as Product;
      const name = componentProduct?.name || '';
      const qty = Math.max(Number(item?.quantity) || 1, 1);
      if (!name) return null;
      // Bundle definitions carry no variant selection.
      return { id: item?.id, name, variants: '', qty };
    })
    .filter(Boolean) as BundleComponentRow[];
};

/**
 * Group line items into bundle parents (with components nested) and regulars.
 */
export const groupBundleLineItems = (items: LineItem[] = []) => {
  const regular: LineItem[] = [];
  const bundleParents: LineItem[] = [];
  const componentsByParent: Record<string, LineItem[]> = {};

  (items || []).forEach(item => {
    // Skip components surfaced at the top level — they're rendered nested.
    if (item?.component_line_item) {
      return;
    }

    const product = (item?.price as Price)?.product as Product | undefined;
    if (product?.bundle) {
      bundleParents.push(item);
      componentsByParent[item.id] = byPosition(item?.component_line_items?.data || []);
      return;
    }

    regular.push(item);
  });

  return { regular, bundleParents, componentsByParent };
};

// Get only enabled price choices.
export const getEnabledPriceChoices = (choices: Array<PriceChoice>): Array<PriceChoice> => {
  return (choices || []).filter(choice => choice?.enabled !== false);
};

// convert price choice to line item data.
export const convertPriceChoiceToLineItemData = (choice: PriceChoice): LineItemData => {
  return {
    price_id: choice.id,
    quantity: choice.quantity,
  };
};

export const convertLineItemsToLineItemData = (
  lineItems: RecursivePartial<lineItems>,
): Array<{
  price_id: string;
  quantity: number;
  variant_id?: string;
}> => {
  return (lineItems?.data || [])
    .filter(item => !item?.component_line_item)
    .map(item => {
      return {
        ...(!!item?.id ? { id: item.id } : {}),
        price_id: item.price.id,
        quantity: item.quantity,
        variant_id: item.variant?.id,
      };
    });
};

export const addLineItem = (lineItems: RecursivePartial<lineItems>, data: { price_id: string; quantity: number }) => {
  const lineItemData = convertLineItemsToLineItemData(lineItems);
  const index = lineItemData.findIndex(item => item.price_id === data.price_id);
  lineItemData[index] = data;
  return lineItemData;
};
/**
 * Calculates the initial line items for the session.
 */
export const calculateInitialLineItems = (choices: Array<PriceChoice>, choiceType: ChoiceType) => {
  // check the url query first.
  const prices = getQueryArg(window.location.href, 'prices') as unknown as Array<PriceChoice>;
  if (prices) {
    return prices.map(convertPriceChoiceToLineItemData);
  }

  // get prices from choices.
  return getInitialChoiceLineItems(choices, choiceType);
};

/**
 * Get the initial choice line items.
 */
export const getInitialChoiceLineItems = (choices: Array<PriceChoice>, choiceType: ChoiceType) => {
  if (choiceType === 'all') {
    return getEnabledPriceChoices(choices).map(convertPriceChoiceToLineItemData);
  } else {
    return [getEnabledPriceChoices(choices).map(convertPriceChoiceToLineItemData)?.[0]];
  }
};

/**
 * Get price ids from line items
 * @param order
 * @returns
 */
export const getLineItemPriceIds = (line_items: RecursivePartial<lineItems>) => {
  return (line_items?.data || []).map(item => item.price.id);
};

export const getLineItemBumpIds = (line_items: RecursivePartial<lineItems>) => {
  return (line_items?.data || []).map(item => (item?.bump as Bump)?.id || (item?.bump as string));
};

export const getLineItemPrices = (line_items: RecursivePartial<lineItems>) => {
  return (line_items?.data || []).map(item => item.price);
};

export const getLineItemByPriceId = (line_items: RecursivePartial<lineItems>, priceId: string) => {
  return (line_items?.data || []).find(item => item.price.id === priceId);
};

/**
 * Is this product in the checkout session?
 */
export const isProductInOrder = (product: RecursivePartial<Product>, order: Checkout) => {
  const prices = getLineItemPrices(order?.line_items);
  if (!prices?.length) return false;
  return !!prices.find(price => (price?.product as Product)?.id === product.id);
};

/**
 * Is the price in a checkout session
 */
export const isPriceInOrder = (price: RecursivePartial<Price>, order: Checkout) => {
  const priceIds = getLineItemPriceIds(order?.line_items);
  return !!priceIds.find(id => price?.id === id);
};

/**
 * Is the price in a checkout session
 */
export const isBumpInOrder = (bump: RecursivePartial<Bump>, order: Checkout) => {
  const bumpIds = getLineItemBumpIds(order?.line_items);
  return !!bumpIds.find(id => bump?.id === id);
};

/**
 * Attempt to get the session id
 *
 * @param formId The form id.
 * @param order The order
 * @param refresh Should we refresh?
 *
 * @returns string
 */
export const getSessionId = (formId, order, refresh = false) => {
  // if we want to get a fresh session, skip
  if (refresh === true) {
    return false;
  }

  // if we already have an ID set, return that:
  if (order?.id) {
    return order.id;
  }

  // check the url query first
  const urlId = getQueryArg(window.location.href, 'order');
  if (urlId) {
    return urlId;
  }

  // check id in localstorage
  return window.localStorage.getItem(formId);
};

/** Check if the order has a subscription */
export const hasSubscription = (order: Checkout) => {
  // no line items.
  if (!order?.line_items?.data?.length) return false;
  // has subscription product.
  return order?.line_items.data?.some(item => item?.price?.recurring_interval_count);
};

export const hasTrial = (order: Checkout) => {
  return order?.trial_amount;
};

/** Check if the order has a payment plan. */
export const hasPaymentPlan = (order: Checkout) => {
  return hasSubscription(order) && order?.line_items.data?.some(item => item?.price?.recurring_period_count);
};
